import {Injectable} from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {EMPTY, Observable} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {IPagedResponse} from '../../../models/paged-bulkfetch/IPagedResponse';
import {
  StudentTicketListViewAsmResponseDTO
} from '../../../models/student-ticket/list-view/StudentTicketListViewAsmResponseDTO';
import {IPageRequest} from '../../../models/paged-bulkfetch/IPageRequest';
import {
  StudentTicketListViewRequestDTO
} from '../../../models/student-ticket/list-view/StudentTicketListViewRequestDTO';
import {TerminationRequestDTO} from "../../../models/student-ticket/termination/TerminationRequestDTO";
import {HttpHeaderService} from "../../utils/http/http-header.service";
import {TerminationConstraintsDTO} from "../../../models/student-ticket/termination/TerminationConstraintsDTO";
import {AddressDataInfoDto} from "../../../models/student-ticket/list-view/AddressDataInfoDto";
import {SchoolDataInfoDto} from "../../../models/student-ticket/list-view/SchoolDataInfoDto";
import {NameDataInfoDto} from "../../../models/student-ticket/list-view/NameDataInfoDto";
import {
  StudentTicketPaymentDataUpdateData
} from "../../../models/student-ticket/list-view/StudentTicketPaymentDataUpdateData";
import {EmailDataInfoDto} from "../../../models/student-ticket/list-view/EmailDataInfoDto";
import {
  StudentTicketBatchUpdateRequestDto
} from "../../../models/student-ticket/verification-wizard/StudentTicketBatchUpdateRequestDto";
import {StudentTicketAttachmentDto} from "../../../models/student-ticket/StudentTicketAttachmentDto";
import {CustomToastService} from "../../utils/custom-toast.service";
import {extractFilenameFromContentDisposition} from "../../utils/content-disposition.util";
import {downloadBlob} from "../../utils/blob-download.util";

@Injectable({
  providedIn: 'root'
})
export class StudentTicketService {

  configUrl = 'api/student-ticket';

  constructor(
    private readonly http: HttpClient,
    private readonly headerService: HttpHeaderService,
    private readonly customToastService: CustomToastService,
  ) {}

  getTicketList(request: IPageRequest<StudentTicketListViewRequestDTO>): Observable<IPagedResponse<StudentTicketListViewAsmResponseDTO>> {
    return this.http.post<IPagedResponse<StudentTicketListViewAsmResponseDTO>>(`${this.configUrl}/bulk`, request, {headers: this.headerService.getAuthHeader()});
  }

  planTermination(uuid: string, request: TerminationRequestDTO): Observable<any> {
    return this.http.post<any>(`${this.configUrl}/${uuid}/plan-termination`, request, {headers: this.headerService.getAuthHeader()});
  }

  cancelTermination(ticketId: string, terminationId: string): Observable<any> {
    return this.http.post<any>(`${this.configUrl}/${ticketId}/cancel-termination/${terminationId}`, null, {headers: this.headerService.getAuthHeader()});
  }

  getTerminationConstraints(uuid: string): Observable<TerminationConstraintsDTO> {
    return this.http.get<TerminationConstraintsDTO>(`${this.configUrl}/termination-constraints/${uuid}`, {headers: this.headerService.getAuthHeader()});
  }

  adjustAddressData(uuid: string, addressDataChangeDto: AddressDataInfoDto): Observable<any> {
    return this.http.post<any>(`${this.configUrl}/${uuid}/update-address-data`,  addressDataChangeDto, {headers: this.headerService.getAuthHeader()});
  }

  adjustSchoolData(uuid: string,  schoolDataChangeDto: SchoolDataInfoDto): Observable<any> {
    return this.http.post<any>(`${this.configUrl}/${uuid}/update-school-data`,  schoolDataChangeDto, {headers: this.headerService.getAuthHeader()});
  }

  adjustNameData(uuid: string,  nameDataChangeDto: NameDataInfoDto): Observable<any> {
    return this.http.post<any>(`${this.configUrl}/${uuid}/update-name-data`,  nameDataChangeDto, {headers: this.headerService.getAuthHeader()});
  }

  adjustGeschwisterData(uuid: string,  paymentDataUpdateDto: StudentTicketPaymentDataUpdateData): Observable<any> {
    return this.http.post<any>(`${this.configUrl}/${uuid}/update-geschwister-data`,  paymentDataUpdateDto, {headers: this.headerService.getAuthHeader()});
  }

  adjustEmailData(uuid: string, emailDataDto: EmailDataInfoDto): Observable<any> {
    return this.http.post<any>(`${this.configUrl}/${uuid}/update-email-data`, emailDataDto, {headers: this.headerService.getAuthHeader()});
  }

  verifyTicket(uuid: string): Observable<string> {
    return this.http.post<string>(`${this.configUrl}/${uuid}/verify`, null, {headers: this.headerService.getAuthHeader()});
  }

  batchUpdate(uuid: string, request: StudentTicketBatchUpdateRequestDto): Observable<string> {
    return this.http.post<string>(`${this.configUrl}/${uuid}/batch-update`, request, {headers: this.headerService.getAuthHeader()});
  }

  getAttachments(ticketId: string): Observable<StudentTicketAttachmentDto[]> {
    return this.http.get<StudentTicketAttachmentDto[]>(`${this.configUrl}/${ticketId}/attachments`, {headers: this.headerService.getAuthHeader()});
  }

  downloadAttachment(ticketId: string, attachmentId: string, fileName?: string): void {
    let downloadFileName = fileName || 'download';
    this.http.get<Blob>(`${this.configUrl}/${ticketId}/attachments/${attachmentId}/download`, {
      headers: this.headerService.getAuthHeader(),
      observe: 'response',
      responseType: 'blob' as 'json'
    })
      .pipe(
        catchError(() => {
          this.customToastService.showError('Der Anhang konnte nicht heruntergeladen werden.');
          return EMPTY;
        })
      )
      .subscribe(response => {
        const contentDisposition = response.headers.get('Content-Disposition');
        downloadFileName = extractFilenameFromContentDisposition(contentDisposition, downloadFileName);
        const blob = (response as HttpResponse<Blob>).body as Blob;
        downloadBlob(blob, downloadFileName);
      });
  }

}
